import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, Mail, ExternalLink, RefreshCw } from "lucide-react";
import api from "../lib/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ contact_count: 0, newsletter_count: 0 });
  const [loading, setLoading] = useState(true);
  const userStr = localStorage.getItem("vk_admin_user");
  const user = userStr ? JSON.parse(userStr) : null;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [m, s, st] = await Promise.all([
        api.get("/admin/contact-submissions"),
        api.get("/admin/newsletter-subscriptions"),
        api.get("/admin/stats"),
      ]);
      setMessages(m.data || []);
      setSubs(s.data || []);
      setStats(st.data || {});
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in.");
        localStorage.removeItem("vk_admin_token");
        localStorage.removeItem("vk_admin_user");
        navigate("/admin/login");
      } else {
        toast.error("Could not load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("vk_admin_token")) {
      navigate("/admin/login");
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    localStorage.removeItem("vk_admin_token");
    localStorage.removeItem("vk_admin_user");
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-cream">
      {/* Topbar */}
      <header className="border-b border-paperline bg-cream sticky top-0 z-30">
        <div className="container-vk h-20 flex items-center justify-between">
          <Link to="/" data-testid="admin-brand" className="heading-display text-xl font-semibold">
            Victor Kwawukume <span className="text-burgundy">/ Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs uppercase tracking-[0.18em] text-ink/60">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={fetchAll}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-paperline px-4 py-2 text-xs uppercase tracking-[0.18em] hover:border-burgundy hover:text-burgundy"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-paperline px-4 py-2 text-xs uppercase tracking-[0.18em] hover:border-burgundy hover:text-burgundy"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="container-vk py-12">
        {/* Stat cards */}
        <div className="grid sm:grid-cols-2 gap-6 mb-12">
          <StatCard
            label="Contact Submissions"
            value={stats.contact_count}
            icon={Inbox}
            testid="stat-contact"
          />
          <StatCard
            label="Newsletter Subscribers"
            value={stats.newsletter_count}
            icon={Mail}
            testid="stat-newsletter"
          />
        </div>

        {/* Tabs */}
        <div className="border-b border-paperline mb-8 flex gap-2">
          <TabButton active={tab === "messages"} onClick={() => setTab("messages")} testid="tab-messages">
            Messages
          </TabButton>
          <TabButton active={tab === "newsletter"} onClick={() => setTab("newsletter")} testid="tab-newsletter">
            Newsletter
          </TabButton>
        </div>

        {loading ? (
          <div className="py-24 text-center text-ink/50">Loading…</div>
        ) : tab === "messages" ? (
          <MessagesTable rows={messages} />
        ) : (
          <NewsletterTable rows={subs} />
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="border border-paperline p-8 bg-cream flex items-center gap-6">
      <Icon size={28} className="text-burgundy" />
      <div>
        <p className="overline">{label}</p>
        <p className="heading-display text-5xl font-semibold mt-1">{value ?? 0}</p>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold border-b-2 -mb-px transition-colors ${
        active ? "border-burgundy text-burgundy" : "border-transparent text-ink/60 hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function MessagesTable({ rows }) {
  if (!rows.length) return <EmptyState label="No messages yet." />;
  return (
    <div className="border border-paperline overflow-x-auto" data-testid="messages-table">
      <table className="w-full text-sm">
        <thead className="bg-cream-deep text-ink/70 uppercase text-xs tracking-[0.14em]">
          <tr>
            <th className="text-left p-4 font-semibold">Date</th>
            <th className="text-left p-4 font-semibold">Name</th>
            <th className="text-left p-4 font-semibold">Email</th>
            <th className="text-left p-4 font-semibold">Subject</th>
            <th className="text-left p-4 font-semibold">Message</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-paperline align-top">
              <td className="p-4 whitespace-nowrap text-ink/60">{new Date(r.created_at).toLocaleString()}</td>
              <td className="p-4 font-medium">{r.name}</td>
              <td className="p-4">
                <a href={`mailto:${r.email}`} className="text-burgundy link-underline">
                  {r.email}
                </a>
              </td>
              <td className="p-4">{r.subject}</td>
              <td className="p-4 text-ink/75 max-w-md">{r.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewsletterTable({ rows }) {
  if (!rows.length) return <EmptyState label="No subscribers yet." />;
  return (
    <div className="border border-paperline overflow-x-auto" data-testid="newsletter-table">
      <table className="w-full text-sm">
        <thead className="bg-cream-deep text-ink/70 uppercase text-xs tracking-[0.14em]">
          <tr>
            <th className="text-left p-4 font-semibold">Subscribed</th>
            <th className="text-left p-4 font-semibold">Email</th>
            <th className="text-left p-4 font-semibold w-20"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-paperline">
              <td className="p-4 whitespace-nowrap text-ink/60">{new Date(r.created_at).toLocaleString()}</td>
              <td className="p-4 font-medium">{r.email}</td>
              <td className="p-4 text-right">
                <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-burgundy">
                  Open <ExternalLink size={12} />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="border border-dashed border-paperline py-24 text-center text-ink/50">
      {label}
    </div>
  );
}
